import { useRef, useState } from 'react';
import { api } from '../api';
import Modal from './Modal';
import { IconCheck, IconDatabase } from './Icons';

// Carga una base completa desde el Excel normalizado: hojas Suscripciones,
// Cuentas y Clientes. Muestra lo que detectó antes de guardar nada.
export default function BaseImport({ open, onClose, onDone }) {
  const [state, setState] = useState('idle'); // idle | reading | preview | saving | done
  const [base, setBase] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setState('idle');
    setBase(null);
    setError('');
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setState('reading');
    setError('');
    if (typeof DecompressionStream === 'undefined') {
      setError('Tu navegador no puede abrir este archivo. Actualízalo o prueba desde Chrome en una computadora.');
      setState('idle');
      return;
    }
    try {
      const { leerBase } = await import('../utils/importNormalizado');
      const leida = await leerBase(file);
      if (!leida.subscriptions.length) throw new Error('El archivo no traía suscripciones con cliente.');
      setBase(leida);
      setState('preview');
    } catch (err) {
      setError(err.message || 'No se pudo leer el archivo.');
      setState('idle');
    }
  };

  const confirm = async () => {
    setState('saving');
    setError('');
    try {
      const { clients, accounts, subscriptions } = base;
      setResult(await api('/import/workbook', { method: 'POST', body: { clients, accounts, subscriptions } }));
      setState('done');
      onDone?.();
    } catch (err) {
      setError(err.message);
      setState('preview');
    }
  };

  const stats = base?.stats;

  return (
    <Modal
      open={open}
      title="Base de datos"
      subtitle="Sube el Excel con las hojas Suscripciones, Cuentas y Clientes."
      size="lg"
      dismissable={false}
      onClose={close}
    >
      {state === 'done' && result ? (
        <div className="workbook-done">
          <p className="settings-success"><IconCheck size={16} /> Carga terminada.</p>
          <dl className="workbook-summary">
            <div><dt>Clientes</dt><dd>{result.clients.created} nuevos · {result.clients.reused} ya existían</dd></div>
            <div><dt>Plataformas</dt><dd>{result.platforms.created} nuevas · {result.platforms.reused} ya existían</dd></div>
            <div><dt>Cuentas del Banco</dt><dd>{result.accounts.created} nuevas · {result.accounts.reused} ya existían</dd></div>
            <div><dt>Suscripciones</dt><dd>{result.subscriptions.created} creadas · {result.subscriptions.skipped} repetidas</dd></div>
          </dl>
          {(result.pendingDate > 0 || result.pendingPrice > 0) && (
            <p className="workbook-warning">
              Quedaron {result.pendingDate} suscripciones sin fecha de vencimiento y {result.pendingPrice} sin precio.
              Complétalas desde Suscripciones; hasta entonces no entran en recordatorios ni cobros.
            </p>
          )}
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={reset}>Subir otro</button>
            <button type="button" className="btn-primary" onClick={close}>Listo</button>
          </div>
        </div>
      ) : state === 'preview' && base ? (
        <div className="workbook-preview">
          <div className="workbook-totals">
            <div><strong>{base.clients.length}</strong><span>clientes</span></div>
            <div><strong>{base.subscriptions.length}</strong><span>suscripciones</span></div>
            <div><strong>{base.accounts.length}</strong><span>cuentas</span></div>
            <div><strong>{stats.platforms}</strong><span>plataformas</span></div>
          </div>

          {(stats.libres > 0 || stats.disponibles > 0) && (
            <p className="workbook-hint">
              {stats.libres > 0 && `${stats.libres} filas sin cliente`}
              {stats.libres > 0 && stats.disponibles > 0 && ' y '}
              {stats.disponibles > 0 && `${stats.disponibles} vencidas sin fecha de inicio`}
              {' '}quedan como perfiles disponibles de sus cuentas, no como ventas.
            </p>
          )}

          {(stats.sinFecha > 0 || stats.sinTelefono > 0 || stats.sinPrecio > 0) && (
            <p className="workbook-warning">
              Del total: {stats.sinFecha} sin fecha de vencimiento, {stats.sinTelefono} clientes sin teléfono
              y {stats.sinPrecio} sin precio. Entran igual y se pueden completar después.
            </p>
          )}

          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={reset}>Elegir otro archivo</button>
            <button type="button" className="btn-primary" onClick={confirm} disabled={state === 'saving'}>
              <IconDatabase size={16} /> {state === 'saving' ? 'Cargando…' : `Cargar ${base.subscriptions.length} suscripciones`}
            </button>
          </div>
        </div>
      ) : (
        <div className="workbook-start">
          <p>
            El archivo debe traer tres hojas: <strong>Suscripciones</strong> (una fila por perfil vendido),
            <strong> Cuentas</strong> (una por correo de streaming) y <strong>Clientes</strong> (una por cliente).
            Es el mismo formato que genera «Exportar operación», así que puedes exportar, editar en Excel y volver a subir.
          </p>
          <p className="workbook-hint">
            Nada se duplica: los clientes, cuentas y suscripciones que ya existan se respetan.
          </p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" onChange={onFile} disabled={state === 'reading'} />
          {state === 'reading' && <p className="workbook-hint">Leyendo el archivo…</p>}
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={close}>Cancelar</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
